import * as Tone from "tone"
import { PersistedData } from "../core/persisted-data"
import { SfxDebug } from "./sfx-debug"
import { ToneSfxSynthesizer } from "./tone-sfx-synthesizer"

// ─────────────────────────────────────────────────────────────
// Set low-latency AudioContext IMMEDIATELY at module level,
// before any Tone.js node constructors run.
// 'interactive' requests the smallest hardware buffer (~128 samples ≈ 2.7ms).
// ─────────────────────────────────────────────────────────────
try {
  Tone.setContext(new Tone.Context({ latencyHint: "interactive" }))
} catch {
  /* keep default if unsupported */
}

let _synth: ToneSfxSynthesizer | null = null
let _lastVolume = -1

function ensureContext(): void {
  const ctx = Tone.getContext()
  if (ctx.state !== "running") {
    // iOS may re-suspend the context — always try to resume
    const rawCtx = ctx.rawContext as AudioContext
    if (rawCtx?.resume) rawCtx.resume()
    Tone.start()
    return
  }
}

function synth(): ToneSfxSynthesizer {
  if (!_synth) {
    _synth = new ToneSfxSynthesizer()
  }
  return _synth
}

/**
 * Unlock the Tone.js AudioContext. MUST be called synchronously inside a user
 * gesture handler — iOS requires resume() to be in the same call stack as the event.
 * Tone.start() is async and may break that requirement, so we call rawContext.resume() directly.
 */
export function unlockAudioContext(): void {
  const rawCtx = Tone.getContext().rawContext as AudioContext
  if (rawCtx.state !== "running") rawCtx.resume()
}

/** Play a sound effect by AUDIO key. `amount` is a per-call intensity multiplier. */
export function playSfx(key: string, amount = 1): void {
  SfxDebug.markPlaySfxEnter(key)
  const vol = PersistedData.getSfxVolume()
  if (vol <= 0) return
  ensureContext()
  SfxDebug.markContextCheck()
  if (vol !== _lastVolume) {
    synth().setVolume(vol)
    _lastVolume = vol
  }
  synth().play(key, amount)
}

/**
 * Initialize the SFX system. Call once from boot-scene.
 * Disposes any previous Tone.js graph to prevent leaked nodes.
 */
export function initSfxPlayer(scene: Phaser.Scene): void {
  // Resume Phaser's WebAudio context if suspended
  const ctx = (scene.sound as any).context as AudioContext | undefined
  if (ctx?.state === "suspended") ctx.resume()

  // Dispose old graph if re-initializing (scene restart / HMR)
  if (_synth) {
    _synth.dispose()
    _synth = null
  }

  _synth = new ToneSfxSynthesizer()
}

// ── Legacy wrapper kept for callers that use getSfxPlayer(scene).play() ──

class SfxPlayer {
  play(key: string, amount?: number): void {
    playSfx(key, amount ?? 1)
  }
}

const _playerFacade = new SfxPlayer()

export function getSfxPlayer(_scene: Phaser.Scene): SfxPlayer {
  synth()
  return _playerFacade
}
